import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Building2, Landmark, FileText, Loader2, RotateCcw, History, X } from 'lucide-react';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const TABS = [
  { id: 'college', label: 'Certificate Header', icon: Building2 },
  { id: 'bank', label: 'Bank Details', icon: Landmark },
  { id: 'fee_structure', label: 'Bonafide Fee Structure', icon: FileText },
];

const CertificateSettings = () => {
  const [activeTab, setActiveTab] = useState('college');
  const [settings, setSettings] = useState({
    college_name: '', college_address: '', principal_name: '',
    signature_url: '', stamp_url: '',
    accreditation_line: '',
    bank_details: { account_name: '', bank_name: '', branch: '', account_no: '', ifsc: '' },
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Bonafide fee structure state
  const [courses, setCourses] = useState([]);
  const [structures, setStructures] = useState([]);
  const [archivedStructures, setArchivedStructures] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [breakdownType, setBreakdownType] = useState('yearly');
  const [entries, setEntries] = useState([{ period: '', fee_head: '', amount: '' }]);
  const [savingFee, setSavingFee] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditCourseFilter, setAuditCourseFilter] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [s, c, fs] = await Promise.all([
        api.get('/certificates/settings'),
        api.get('/master-settings/courses'),
        api.get('/certificates/bonafide-fee-structure', { params: { include_archived: true } }),
      ]);
      setSettings(prev => ({
        ...prev,
        ...s.data,
        bank_details: { ...prev.bank_details, ...(s.data?.bank_details || {}) },
      }));
      setCourses(Array.isArray(c.data) ? c.data : (c.data?.data || []));
      const all = Array.isArray(fs.data) ? fs.data : (fs.data?.data || []);
      setStructures(all.filter(x => x.is_active !== false));
      setArchivedStructures(all.filter(x => x.is_active === false));
    } catch (e) {
      console.error('Failed to load certificate settings:', e);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image too large. Please upload <= 2MB.');
      return;
    }
    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file, { photoMaxDim: 800, quality: 0.85 });
      setSettings(prev => ({ ...prev, [field]: result.data }));
    } catch (err) {
      console.error('File read failed:', err);
      alert('Failed to read file.');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post('/certificates/settings', settings);
      alert('Settings saved successfully!');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadStructureForCourse = async (courseId) => {
    setSelectedCourse(courseId);
    if (!courseId) {
      setEntries([{ period: '', fee_head: '', amount: '' }]);
      setBreakdownType('yearly');
      return;
    }
    try {
      const r = await api.get(`/certificates/bonafide-fee-structure/${courseId}`);
      setBreakdownType(r.data?.breakdown_type || 'yearly');
      const ents = (r.data?.entries || []).map(e => ({ period: e.period || '', fee_head: e.fee_head || '', amount: e.amount ?? '' }));
      setEntries(ents.length ? ents : [{ period: '', fee_head: '', amount: '' }]);
    } catch (e) {
      console.error(e);
    }
  };

  const updateEntry = (idx, key, value) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, [key]: value } : e));
  };
  const addEntryRow = () => setEntries(prev => [...prev, { period: '', fee_head: '', amount: '' }]);
  const removeEntryRow = (idx) => setEntries(prev => prev.filter((_, i) => i !== idx));

  const periodSuggestions = breakdownType === 'yearly'
    ? ['1st Year', '2nd Year', '3rd Year', '4th Year']
    : ['Semester 1', 'Semester 2', 'Semester 3', 'Semester 4', 'Semester 5', 'Semester 6', 'Semester 7', 'Semester 8'];

  const saveFeeStructure = async () => {
    if (!selectedCourse) { alert('Please select a course first.'); return; }
    const cleaned = entries.filter(e => e.period && e.fee_head);
    setSavingFee(true);
    try {
      await api.post('/certificates/bonafide-fee-structure', {
        course_id: selectedCourse,
        breakdown_type: breakdownType,
        entries: cleaned.map(e => ({ ...e, amount: Number(e.amount) || 0 })),
      });
      alert('Fee structure saved!');
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save fee structure');
    } finally {
      setSavingFee(false);
    }
  };

  const deleteFeeStructure = async (courseId) => {
    if (!window.confirm('Archive fee structure for this course? It can be restored later.')) return;
    try {
      await api.delete(`/certificates/bonafide-fee-structure/${courseId}`);
      await fetchAll();
      if (selectedCourse === courseId) {
        setSelectedCourse('');
        setEntries([{ period: '', fee_head: '', amount: '' }]);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to archive');
    }
  };

  const restoreFeeStructure = async (courseId) => {
    if (!window.confirm('Restore this archived fee structure?')) return;
    try {
      await api.post(`/certificates/bonafide-fee-structure/${courseId}/restore`);
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to restore');
    }
  };

  const openAuditLog = async (courseId = null) => {
    setAuditCourseFilter(courseId);
    setShowAuditModal(true);
    try {
      const res = await api.get('/certificates/bonafide-fee-structure-audit', {
        params: courseId ? { course_id: courseId } : {},
      });
      setAuditLog(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load audit log:', e);
      setAuditLog([]);
    }
  };

  const courseName = (id) => courses.find(c => c.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="certificate-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Certificate Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Configure college info, bank details and bonafide fee structure used in printed certificates.</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 px-4 flex gap-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                data-testid={`cert-settings-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'college' && (
          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border-l-4 border-blue-400 px-4 py-2 rounded">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> College Name and Address are auto-pulled from your College Profile Settings.
                Only fields specific to certificates are configured here.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Principal Name</label>
                <input type="text" value={settings.principal_name || ''}
                  onChange={e => setSettings({ ...settings, principal_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" data-testid="cert-principal-name" />
                <p className="text-xs text-gray-500 mt-1">Shown below the signature on certificates.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accreditation Line</label>
                <input type="text" value={settings.accreditation_line || ''}
                  onChange={e => setSettings({ ...settings, accreditation_line: e.target.value })}
                  placeholder="e.g., Approved By INC; Affiliated By W.B.N.C; WBUHS"
                  className="w-full px-3 py-2 border rounded-lg" data-testid="cert-accreditation-line" />
                <p className="text-xs text-gray-500 mt-1">Shown above the address on the certificate header.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Principal Signature (PNG)</label>
                <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'signature_url')}
                  className="w-full text-sm" data-testid="cert-signature-upload" />
                {settings.signature_url && (
                  <img src={settings.signature_url} alt="signature" className="h-12 mt-2 border rounded p-1" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Official Stamp (PNG)</label>
                <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'stamp_url')}
                  className="w-full text-sm" data-testid="cert-stamp-upload" />
                {settings.stamp_url && (
                  <img src={settings.stamp_url} alt="stamp" className="h-12 mt-2 border rounded p-1" />
                )}
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t">
              <button onClick={saveSettings} disabled={savingSettings}
                data-testid="cert-save-college"
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Certificate Info
              </button>
            </div>
          </div>
        )}

        {activeTab === 'bank' && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-500">These details appear on the Bonafide Certificate when "Include Bank Details" is enabled.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { k: 'account_name', label: 'Account Name', testid: 'bank-account-name' },
                { k: 'bank_name', label: 'Bank Name', testid: 'bank-name' },
                { k: 'branch', label: 'Branch', testid: 'bank-branch' },
                { k: 'account_no', label: 'A/C No.', testid: 'bank-acc-no' },
                { k: 'ifsc', label: 'IFSC Code', testid: 'bank-ifsc' },
              ].map(f => (
                <div key={f.k}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                  <input type="text" value={settings.bank_details?.[f.k] || ''}
                    onChange={e => setSettings({ ...settings, bank_details: { ...(settings.bank_details || {}), [f.k]: e.target.value } })}
                    className="w-full px-3 py-2 border rounded-lg" data-testid={f.testid} />
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-4 border-t">
              <button onClick={saveSettings} disabled={savingSettings}
                data-testid="cert-save-bank"
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Bank Details
              </button>
            </div>
          </div>
        )}

        {activeTab === 'fee_structure' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                <select value={selectedCourse} onChange={e => loadStructureForCourse(e.target.value)}
                  data-testid="fee-struct-course"
                  className="w-full px-3 py-2 border rounded-lg">
                  <option value="">— Select Course —</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Breakdown Type</label>
                <select value={breakdownType} onChange={e => setBreakdownType(e.target.value)}
                  data-testid="fee-struct-breakdown"
                  className="w-full px-3 py-2 border rounded-lg">
                  <option value="yearly">Year-wise</option>
                  <option value="semester-wise">Semester-wise</option>
                </select>
              </div>
            </div>

            {selectedCourse && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Fee Entries</h3>
                  <button onClick={addEntryRow} data-testid="fee-struct-add-row"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                    <Plus className="w-4 h-4" /> Add Row
                  </button>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Period</th>
                        <th className="px-3 py-2 text-left">Fee Head</th>
                        <th className="px-3 py-2 text-right">Amount (Rs.)</th>
                        <th className="px-3 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">
                            <input list={`periods-${i}`} value={e.period}
                              onChange={ev => updateEntry(i, 'period', ev.target.value)}
                              className="w-full px-2 py-1 border rounded" placeholder="e.g. 1st Year" />
                            <datalist id={`periods-${i}`}>
                              {periodSuggestions.map(p => <option key={p} value={p} />)}
                            </datalist>
                          </td>
                          <td className="px-2 py-1">
                            <input value={e.fee_head} onChange={ev => updateEntry(i, 'fee_head', ev.target.value)}
                              className="w-full px-2 py-1 border rounded" placeholder="e.g. Tuition Fee" />
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" value={e.amount} onChange={ev => updateEntry(i, 'amount', ev.target.value)}
                              className="w-full px-2 py-1 border rounded text-right" placeholder="0" min="0" />
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button onClick={() => removeEntryRow(i)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end pt-4">
                  <button onClick={saveFeeStructure} disabled={savingFee}
                    data-testid="fee-struct-save"
                    className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {savingFee ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Fee Structure
                  </button>
                </div>
              </div>
            )}

            {(structures.length > 0 || archivedStructures.length > 0) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-gray-700">Configured Courses</h3>
                    {archivedStructures.length > 0 && (
                      <button
                        onClick={() => setShowArchived(v => !v)}
                        className="text-xs text-gray-600 hover:text-gray-800 underline"
                        data-testid="toggle-archived"
                      >
                        {showArchived ? 'Hide' : `Show ${archivedStructures.length} archived`}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => openAuditLog(null)}
                    data-testid="open-audit-log"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    <History className="w-3 h-3" /> Audit Log
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {structures.map(s => (
                    <div key={s.course_id} className="flex items-center justify-between border rounded-lg px-4 py-2">
                      <div>
                        <p className="font-medium text-sm">{courseName(s.course_id)}</p>
                        <p className="text-xs text-gray-500">{s.breakdown_type} · {(s.entries || []).length} entries</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => loadStructureForCourse(s.course_id)} className="text-xs text-blue-600 hover:text-blue-700">Edit</button>
                        <button onClick={() => openAuditLog(s.course_id)} className="text-xs text-gray-500 hover:text-gray-700" title="Audit log for this course">
                          <History className="w-3 h-3" />
                        </button>
                        <button onClick={() => deleteFeeStructure(s.course_id)} className="text-xs text-red-600 hover:text-red-700">Archive</button>
                      </div>
                    </div>
                  ))}
                </div>

                {showArchived && archivedStructures.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Archived</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {archivedStructures.map(s => (
                        <div key={s.course_id} className="flex items-center justify-between border border-dashed border-gray-300 bg-gray-50 rounded-lg px-4 py-2">
                          <div>
                            <p className="font-medium text-sm text-gray-700">{courseName(s.course_id)}</p>
                            <p className="text-xs text-gray-500">
                              archived {s.deleted_at ? formatDate(s.deleted_at) : ''} by {s.deleted_by || 'unknown'}
                            </p>
                          </div>
                          <button
                            onClick={() => restoreFeeStructure(s.course_id)}
                            data-testid={`restore-${s.course_id}`}
                            className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                          >
                            <RotateCcw className="w-3 h-3" /> Restore
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Audit Log Modal */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAuditModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <History className="w-4 h-4" />
                Audit Log {auditCourseFilter ? `· ${courseName(auditCourseFilter)}` : '(all courses)'}
              </h3>
              <button onClick={() => setShowAuditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {auditLog.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No audit entries yet.</p>
              ) : (
                <div className="space-y-2">
                  {auditLog.map((log, i) => {
                    const colors = {
                      create: 'text-green-700 bg-green-50',
                      update: 'text-blue-700 bg-blue-50',
                      delete: 'text-red-700 bg-red-50',
                      restore: 'text-purple-700 bg-purple-50',
                    };
                    return (
                      <div key={i} className="flex items-start gap-3 border-b pb-2 last:border-0">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${colors[log.action] || 'text-gray-700 bg-gray-100'}`}>
                          {log.action.toUpperCase()}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800">
                            {courseName(log.course_id)}
                          </p>
                          <p className="text-xs text-gray-500">
                            by <span className="font-medium">{log.actor_email}</span> · {log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN') : ''}
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {log.details.breakdown_type ? `${log.details.breakdown_type}, ` : ''}
                              {log.details.entry_count != null ? `${log.details.entry_count} entries` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateSettings;
